<template>
  <modal
    :show.sync="isOpen"
    modal-classes="modal-dialog-centered modal-lg"
    body-classes
    @close="onClose"
  >
    <template slot="header">
      <h5 class="subtitle" id="addProductsModal">Add Products</h5>
    </template>

    <div class="row align-items-center mt-3 forms">
      <div class="col-md-6 col-12">
        <WSelect
          v-model="destinationSlug"
          filterable
          clearable
          label="Destination"
          placeholder="Select a destination"
          @change="loadProducts"
        >
          <el-option
            v-for="destination in destinationsOptions"
            :key="destination.slug"
            :value="destination.slug"
            :label="`${destination.city} - ${getCountryName(destination)}`"
          >
            <span class="hide-data-inline coin-left">{{
              destination.city
            }}</span>
            <span class="hide-data-inline coin-right">{{
              getCountryName(destination)
            }}</span>
          </el-option>
        </WSelect>
      </div>
      <div class="col-md-6 col-12">
        <base-input
          v-model.trim="search"
          label="Search"
          placeholder="Product name or code"
          @input="debouncedLoadProducts"
        />
      </div>
    </div>

    <div class="product-list border rounded mt-2">
      <div v-if="loading" class="text-center text-muted py-4">
        <b-spinner small class="mr-1" /> Loading products...
      </div>

      <p v-else-if="!hasFilters" class="text-center text-muted py-4 mb-0">
        Select a destination or search a product.
      </p>

      <p v-else-if="products.length === 0" class="text-center text-muted py-4 mb-0">
        No products found.
      </p>

      <template v-else>
        <div class="product-row product-row-header">
          <b-form-checkbox
            :checked="allChecked"
            :indeterminate="someChecked"
            :disabled="selectableProducts.length === 0"
            @change="toggleAll"
          >
            <strong>Select all ({{ selectableProducts.length }})</strong>
          </b-form-checkbox>
        </div>
        <div
          class="product-row"
          v-for="product in products"
          :key="product.uuid"
        >
          <b-form-checkbox
            :checked="isAdded(product.uuid) || isChecked(product.uuid)"
            :disabled="!isSelectable(product)"
            @change="toggleProduct(product, $event)"
          >
            {{ product.name }}
            <span class="text-muted f-12" v-if="isAdded(product.uuid)">
              (added)
            </span>
          </b-form-checkbox>
          <div
            class="badge-status"
            :class="{
              'badge-success': isActive(product),
              'badge-danger': !isActive(product)
            }"
          >
            <span v-if="isActive(product)">Active</span>
            <span v-else>Inactive</span>
          </div>
        </div>
      </template>
    </div>

    <p class="text-muted f-12 mt-2 mb-0" v-if="hasMoreResults">
      Showing the first {{ products.length }} of {{ total }} products. Refine the
      search to find the rest.
    </p>

    <template slot="footer">
      <base-button
        type="outline-neutral"
        class="min-button mb-0"
        @click="isOpen = false"
        ><i class="flaticon-cancel"></i>Cancel</base-button
      >

      <base-button
        type="primary"
        class="min-button mb-0 ml-auto"
        :disabled="checkedProducts.length === 0"
        @click="addProducts()"
        ><i class="flaticon-plus"></i>Add {{ checkedProducts.length }}
        {{ checkedProducts.length === 1 ? "product" : "products" }}</base-button
      >
    </template>
  </modal>
</template>

<script>
import { mapGetters } from "vuex";
import debounce from "lodash.debounce";
import { DestinationsAPI, ProductsAPI } from "../../../core/api";
import WSelect from "@/components/Select";

const MIN_SEARCH_LENGTH = 2;
const MAX_RESULTS = 100;
const ACTIVE_SERVICE_STATUS = "ACTIVE";

export default {
  name: "ResellerProductPicker",
  components: {
    WSelect
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    addedUuids: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      destinationsOptions: [],
      destinationSlug: "",
      search: "",
      products: [],
      total: 0,
      loading: false,
      checkedProducts: []
    };
  },
  async created() {
    this.debouncedLoadProducts = debounce(this.loadProducts, 400);
    await this.loadDestinations();
  },
  beforeDestroy() {
    this.debouncedLoadProducts.cancel();
  },
  methods: {
    async loadDestinations() {
      try {
        let { data } = await DestinationsAPI.getDestinationsOptions(
          this.sesion.token
        );
        const destinations = data && data.status ? data.data : [];
        this.destinationsOptions = [...destinations].sort((a, b) =>
          a.city.localeCompare(b.city)
        );
      } catch (error) {
        this.destinationsOptions = [];
      }
    },
    async loadProducts() {
      if (!this.hasFilters) {
        this.products = [];
        this.total = 0;
        this.keepVisibleChecked();
        return;
      }
      this.loading = true;
      try {
        let { data } = await ProductsAPI.getProducts(
          MAX_RESULTS,
          1,
          this.buildQuery(),
          this.sesion.token
        );
        const page = data && data.data ? data.data : null;
        this.products = page ? page.docs : [];
        this.total = page ? page.count : 0;
      } catch (error) {
        this.products = [];
        this.total = 0;
      } finally {
        this.keepVisibleChecked();
        this.loading = false;
      }
    },
    /**
     * Keeps checked only the products still listed after a filter change, so
     * "Add N products" never adds products the user can no longer see.
     */
    keepVisibleChecked() {
      this.checkedProducts = this.checkedProducts.filter(checked =>
        this.products.some(product => product.uuid === checked.uuid)
      );
    },
    buildQuery() {
      const params = [];
      if (this.destinationSlug) {
        params.push(`destinations=${encodeURIComponent(this.destinationSlug)}`);
      }
      if (this.search.length >= MIN_SEARCH_LENGTH) {
        params.push(`search=${encodeURIComponent(this.search)}`);
      }
      return params.join("&");
    },
    isAdded(uuid) {
      return this.addedUuids.includes(uuid);
    },
    /**
     * Same rule the API uses to expose a product: active and with an active service.
     * Inactive products are listed for reference but cannot be selected.
     */
    isActive(product) {
      return (
        product.status === true &&
        !!product.service &&
        product.service.status === ACTIVE_SERVICE_STATUS
      );
    },
    isSelectable(product) {
      return !this.isAdded(product.uuid) && this.isActive(product);
    },
    isChecked(uuid) {
      return this.checkedProducts.some(product => product.uuid === uuid);
    },
    toggleProduct(product, checked) {
      if (checked && !this.isChecked(product.uuid) && this.isSelectable(product)) {
        this.checkedProducts.push(product);
      } else if (!checked) {
        this.checkedProducts = this.checkedProducts.filter(
          item => item.uuid !== product.uuid
        );
      }
    },
    toggleAll(checked) {
      this.selectableProducts.forEach(product =>
        this.toggleProduct(product, checked)
      );
    },
    addProducts() {
      this.$emit("add", this.checkedProducts);
      this.isOpen = false;
    },
    onClose() {
      this.checkedProducts = [];
    },
    getCountryName(destination) {
      return destination.country && destination.country.name
        ? destination.country.name
        : "";
    }
  },
  computed: {
    ...mapGetters(["sesion"]),
    isOpen: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit("update:show", value);
      }
    },
    hasFilters() {
      return !!this.destinationSlug || this.search.length >= MIN_SEARCH_LENGTH;
    },
    hasMoreResults() {
      return !this.loading && this.total > this.products.length;
    },
    selectableProducts() {
      return this.products.filter(product => this.isSelectable(product));
    },
    allChecked() {
      return (
        this.selectableProducts.length > 0 &&
        this.selectableProducts.every(product => this.isChecked(product.uuid))
      );
    },
    someChecked() {
      return (
        !this.allChecked &&
        this.selectableProducts.some(product => this.isChecked(product.uuid))
      );
    }
  }
};
</script>

<style scoped>
.product-list {
  max-height: 360px;
  overflow-y: auto;
}
.product-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e9ecef;
}
/* The theme fixes the checkbox label to 1rem high, so long product names
   wrapped onto a second line overlapped the next row. */
.product-row >>> .custom-control {
  flex: 1;
  min-width: 0;
  margin-right: 1rem;
}
.product-row >>> .custom-control-label {
  height: auto;
  line-height: 18px;
}
.product-row .badge-status {
  flex-shrink: 0;
}
.product-row:last-child {
  border-bottom: none;
}
.product-row-header {
  background: #f8f9fe;
}
</style>
